import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Upload, 
  Plus, 
  Trash2, 
  Edit, 
  Save, 
  X, 
  FileSpreadsheet, 
  TrendingUp,
  Loader2,
  CheckCircle,
  AlertCircle,
  Download,
  Filter,
  Search,
  Bug
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const IndustryBenchmarkManager = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterAgent, setFilterAgent] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDiagnostic, setShowDiagnostic] = useState(false);
  const [diagnosticKpi, setDiagnosticKpi] = useState('');
  const [diagnosticIndustry, setDiagnosticIndustry] = useState('');
  const [diagnosticResult, setDiagnosticResult] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [editingBenchmark, setEditingBenchmark] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);
  const [newBenchmark, setNewBenchmark] = useState({
    industry: '',
    business_model: '',
    operational_structure: '',
    category: '',
    indicator_name: '',
    benchmark_value: '',
    source: '',
    agent_name: '',
    module_type: '',
    metric_type: 'number',
    comparison_operator: 'gte',
    description: ''
  });

  const token = localStorage.getItem('token');

  // Fetch benchmarks
  const { data: benchmarks = [], isLoading, refetch } = useQuery({
    queryKey: ['industry-benchmarks', filterIndustry, filterAgent],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterAgent) params.append('agent_name', filterAgent);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/api/admin/industry-benchmarks${queryString}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  // Fetch unique industries
  const { data: industriesData } = useQuery({
    queryKey: ['benchmark-industries'],
    queryFn: async () => {
      const response = await axios.get(`${API}/api/admin/industry-benchmarks/industries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  // Fetch unique agents from benchmarks
  const { data: benchmarkAgentsData } = useQuery({
    queryKey: ['benchmark-agents'],
    queryFn: async () => {
      const response = await axios.get(`${API}/api/admin/industry-benchmarks/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    enabled: !!token
  });

  // Fetch agents for dropdown
  const { data: agents = [] } = useQuery({
    queryKey: ['all-agents'],
    queryFn: async () => {
      try {
        const response = await axios.get(`${API}/api/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        return response.data || [];
      } catch (error) {
        console.error('Error fetching agents:', error);
        return [];
      }
    },
    enabled: !!token,
    retry: false
  });

  const industries = industriesData?.industries || [];
  const benchmarkAgents = benchmarkAgentsData?.agents || [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, replaceAll: shouldReplace }) => {
      const formData = new FormData();
      formData.append('file', file);
      
      const url = shouldReplace 
        ? `${API}/api/admin/industry-benchmarks/upload?replace_all=true`
        : `${API}/api/admin/industry-benchmarks/upload`;
      
      const response = await axios.post(
        url,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000 // 3 minute timeout for large files
        }
      );
      return response.data;
    },
    onSuccess: (data) => {
      setUploadResult(data);
      queryClient.invalidateQueries(['industry-benchmarks']);
      queryClient.invalidateQueries(['benchmark-industries']);
      if (data.created > 0 || data.updated > 0) {
        toast.success(`${data.created} benchmarks creados, ${data.updated} actualizados`);
      }
      setSelectedFile(null);
      setReplaceAll(false);
    },
    onError: (error) => {
      // Check if it's a timeout error - data might have been processed
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.response?.status === 524) {
        toast.warning('La carga tomó más tiempo del esperado. Verifica si los datos se cargaron correctamente.');
        queryClient.invalidateQueries(['industry-benchmarks']);
        queryClient.invalidateQueries(['benchmark-industries']);
      } else {
        toast.error(error.response?.data?.detail || 'Error al cargar archivo');
      }
      setUploadResult(null);
    }
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data) => {
      const response = await axios.post(`${API}/api/admin/industry-benchmarks`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Benchmark creado');
      queryClient.invalidateQueries(['industry-benchmarks']);
      queryClient.invalidateQueries(['benchmark-industries']);
      setShowAddDialog(false);
      setNewBenchmark({
        industry: '',
        sub_industry: '',
        business_model: '',
        indicator_name: '',
        benchmark_value: '',
        agent_name: '',
        metric_type: 'number',
        comparison_operator: 'gte',
        description: ''
      });
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al crear benchmark');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await axios.put(`${API}/api/admin/industry-benchmarks/${id}`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Benchmark actualizado');
      queryClient.invalidateQueries(['industry-benchmarks']);
      setEditingBenchmark(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al actualizar benchmark');
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const response = await axios.delete(`${API}/api/admin/industry-benchmarks/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success('Benchmark eliminado');
      queryClient.invalidateQueries(['industry-benchmarks']);
      queryClient.invalidateQueries(['benchmark-industries']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || 'Error al eliminar benchmark');
    }
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadResult(null);
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      if (replaceAll) {
        if (!window.confirm('¿Estás seguro? Esto eliminará TODOS los benchmarks existentes y cargará los nuevos.')) {
          return;
        }
      }
      uploadMutation.mutate({ file: selectedFile, replaceAll });
    }
  };

  const handleCreateBenchmark = () => {
    if (!newBenchmark.industry || !newBenchmark.indicator_name || !newBenchmark.benchmark_value) {
      toast.error('Complete los campos requeridos');
      return;
    }
    createMutation.mutate(newBenchmark);
  };

  const handleUpdateBenchmark = () => {
    if (editingBenchmark) {
      updateMutation.mutate({
        id: editingBenchmark.id,
        data: editingBenchmark
      });
    }
  };

  const handleDeleteBenchmark = (id) => {
    if (window.confirm('¿Estás seguro de eliminar este benchmark?')) {
      deleteMutation.mutate(id);
    }
  };

  const downloadTemplate = () => {
    const csvContent = `Industria,Modelo_Negocio,Estructura_Operativa,Categoria,indicador_de_mercado,valor_benchmark,Fuente,agente,Modulo
Retail,B2C,Tienda Física,Ventas,Ticket Promedio,150,Industry Report 2024,Sales Performance Tracker,revenue
Retail,B2B,Distribución,Logística,Rotación de Inventario,12,McKinsey Analysis,Supply Chain Agent,supply_chain
Tecnología,SaaS,Cloud,Marketing,Tasa de Conversión,3.5,SaaS Benchmarks 2024,Revenue Analytics Agent,revenue
Tecnología,Consultoría,Servicios,Retención,Churn Rate,5.0,Gartner Report,Customer Success Agent,operations
Manufactura,Producción,Planta,Eficiencia,OEE,85,Industry Standard,Operations Agent,operations
Servicios,Financieros,Banca,Satisfacción,NPS,50,Banking Survey 2024,Customer Experience Agent,operations`;
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'plantilla_benchmarks_v4.csv';
    link.click();
  };

  const runDiagnostic = async () => {
    if (!diagnosticKpi.trim()) {
      toast.error('Ingresa un nombre de KPI para diagnosticar');
      return;
    }
    setDiagLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('kpi_name', diagnosticKpi.trim());
      if (diagnosticIndustry) {
        params.append('industry', diagnosticIndustry);
      }
      const response = await axios.get(
        `${API}/api/admin/benchmark-diagnostic?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDiagnosticResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar diagnóstico');
      setDiagnosticResult(null);
    } finally {
      setDiagLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-white">Benchmarks de Industria</CardTitle>
              <CardDescription className="text-slate-400">
                Configura métricas de referencia por industria para comparar automáticamente con los resultados de los agentes
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Upload Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            Cargar Benchmarks desde Excel
          </CardTitle>
          <CardDescription className="text-slate-400">
            Sube un archivo Excel o CSV con las columnas: Industria, Sub-Industria, Modelo Negocio, indicador_de_mercado, valor_benchmark, agente, Modulo, Fuente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileSelect}
                  className="bg-slate-900 border-slate-600 text-white file:bg-slate-700 file:text-white file:border-0 file:mr-4"
                />
              </div>
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploadMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {uploadMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Cargar
              </Button>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="border-slate-600 text-slate-300 hover:bg-slate-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Plantilla
              </Button>
            </div>

            {/* Replace All Option */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="replaceAll"
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                className="w-4 h-4 rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
              />
              <label htmlFor="replaceAll" className="text-sm text-slate-300">
                Reemplazar todos los benchmarks existentes (elimina los anteriores antes de cargar)
              </label>
            </div>

            {selectedFile && (
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <FileSpreadsheet className="w-4 h-4" />
                {selectedFile.name}
              </div>
            )}

            {uploadResult && (
              <div className={`p-4 rounded-lg ${uploadResult.total_errors > 0 ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-emerald-500/10 border border-emerald-500/30'}`}>
                <div className="flex items-start gap-3">
                  {uploadResult.total_errors > 0 ? (
                    <AlertCircle className="w-5 h-5 text-yellow-400 mt-0.5" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium text-white">
                      {uploadResult.created} creados, {uploadResult.updated} actualizados
                    </p>
                    {uploadResult.errors && uploadResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm text-yellow-400 mb-1">Errores ({uploadResult.total_errors}):</p>
                        <ul className="text-xs text-slate-400 space-y-1">
                          {uploadResult.errors.map((err, i) => (
                            <li key={i}>• {err}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Filters and Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400" />
            <Select value={filterIndustry || "all"} onValueChange={(v) => setFilterIndustry(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Todas las industrias" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">Todas las industrias</SelectItem>
                {industries.map((ind) => (
                  <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Agent Filter */}
          <div className="flex items-center gap-2">
            <Select value={filterAgent || "all"} onValueChange={(v) => setFilterAgent(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Todos los agentes" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 max-h-80">
                <SelectItem value="all">Todos los agentes</SelectItem>
                <SelectItem value="__none__">Sin agente asignado</SelectItem>
                {benchmarkAgents.map((agent) => (
                  <SelectItem key={agent.name} value={agent.name}>
                    {agent.name} ({agent.count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Badge className="bg-slate-700 text-slate-300">
            {benchmarks.length} benchmarks
          </Badge>
          
          {(filterIndustry || filterAgent) && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => { setFilterIndustry(''); setFilterAgent(''); }}
              className="text-slate-400 hover:text-white"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar filtros
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowDiagnostic(!showDiagnostic)}
            className="border-amber-600 text-amber-400 hover:bg-amber-600/20"
          >
            <Bug className="w-4 h-4 mr-2" />
            Diagnosticar Matching
          </Button>
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Agregar Benchmark
          </Button>
        </div>
      </div>

      {/* Diagnostic Tool */}
      {showDiagnostic && (
        <Card className="bg-amber-900/20 border-amber-500/30">
          <CardHeader>
            <CardTitle className="text-amber-400 flex items-center gap-2">
              <Bug className="w-5 h-5" />
              Herramienta de Diagnóstico de Matching
            </CardTitle>
            <CardDescription className="text-slate-400">
              Ingresa el nombre de un KPI para ver por qué no está coincidiendo con ningún benchmark
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Label className="text-slate-300 mb-2 block">Nombre del KPI (como aparece en el Excel)</Label>
                  <Input
                    value={diagnosticKpi}
                    onChange={(e) => setDiagnosticKpi(e.target.value)}
                    placeholder="Ej: capital_trabajo_pct_activos, Current Ratio, ROE"
                    className="bg-slate-900 border-slate-600 text-white"
                  />
                </div>
                <div className="w-48">
                  <Label className="text-slate-300 mb-2 block">Industria (opcional)</Label>
                  <Select value={diagnosticIndustry || "all"} onValueChange={(v) => setDiagnosticIndustry(v === "all" ? "" : v)}>
                    <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="all">Todas las industrias</SelectItem>
                      {industries.map((ind) => (
                        <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={runDiagnostic}
                    disabled={diagLoading}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    {diagLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Search className="w-4 h-4 mr-2" />
                    )}
                    Diagnosticar
                  </Button>
                </div>
              </div>

              {diagnosticResult && (
                <div className="mt-4 space-y-4">
                  {/* KPI Info */}
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <h4 className="text-white font-medium mb-2">Análisis del KPI</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-400">Original:</span>
                        <span className="text-white ml-2 font-mono">{diagnosticResult.kpi_input}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Lowercase:</span>
                        <span className="text-white ml-2 font-mono">{diagnosticResult.kpi_lowercase}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Normalizado:</span>
                        <span className="text-white ml-2 font-mono">{diagnosticResult.kpi_normalized}</span>
                      </div>
                      <div>
                        <span className="text-slate-400">Keywords:</span>
                        <span className="text-white ml-2 font-mono">{diagnosticResult.kpi_keywords?.join(', ')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Results Summary */}
                  <div className="flex gap-4">
                    <Badge className={diagnosticResult.exact_matches?.length > 0 ? 'bg-green-600' : 'bg-slate-600'}>
                      {diagnosticResult.exact_matches?.length || 0} coincidencias exactas
                    </Badge>
                    <Badge className={diagnosticResult.would_match_count > 0 ? 'bg-blue-600' : 'bg-slate-600'}>
                      {diagnosticResult.would_match_count || 0} coincidirían con nuevo algoritmo
                    </Badge>
                    <Badge className="bg-slate-600">
                      {diagnosticResult.total_benchmarks_searched} benchmarks revisados
                    </Badge>
                  </div>

                  {/* Potential Matches */}
                  {diagnosticResult.potential_matches?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <h4 className="text-white font-medium mb-2">Coincidencias Potenciales</h4>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-slate-400 border-b border-slate-700">
                            <th className="text-left p-2">Indicador en Benchmark</th>
                            <th className="text-left p-2">Industria</th>
                            <th className="text-left p-2">Valor</th>
                            <th className="text-center p-2">Exacto</th>
                            <th className="text-center p-2">Normalizado</th>
                            <th className="text-center p-2">Keywords</th>
                            <th className="text-center p-2">¿Coincidiría?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosticResult.potential_matches.map((match, idx) => (
                            <tr key={idx} className={`border-b border-slate-700/50 ${match.would_match ? 'bg-green-900/20' : ''}`}>
                              <td className="p-2 text-white font-mono text-xs">{match.benchmark_indicator}</td>
                              <td className="p-2 text-slate-300">{match.industry}</td>
                              <td className="p-2 text-slate-300">{match.benchmark_value}</td>
                              <td className="p-2 text-center">{match.exact_match ? '✅' : '❌'}</td>
                              <td className="p-2 text-center">{match.normalized_match ? '✅' : '❌'}</td>
                              <td className="p-2 text-center">{(match.keyword_score * 100).toFixed(0)}%</td>
                              <td className="p-2 text-center">{match.would_match ? '✅ SÍ' : '❌ NO'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                      <p className="text-red-400">
                        No se encontraron coincidencias potenciales. Verifica que los nombres de los indicadores en tus benchmarks sean similares al KPI que buscas.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Benchmarks Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : benchmarks.length === 0 ? (
            <div className="text-center p-12">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-slate-600" />
              <p className="text-slate-400">No hay benchmarks configurados</p>
              <p className="text-sm text-slate-500 mt-1">Carga un archivo Excel o agrega benchmarks manualmente</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-slate-700 hover:bg-transparent">
                  <TableHead className="text-slate-400">Industria</TableHead>
                  <TableHead className="text-slate-400">Modelo Negocio</TableHead>
                  <TableHead className="text-slate-400">Estructura Op.</TableHead>
                  <TableHead className="text-slate-400">Categoría</TableHead>
                  <TableHead className="text-slate-400">Indicador</TableHead>
                  <TableHead className="text-slate-400">Benchmark</TableHead>
                  <TableHead className="text-slate-400">Fuente</TableHead>
                  <TableHead className="text-slate-400">Agente</TableHead>
                  <TableHead className="text-slate-400">Módulo</TableHead>
                  <TableHead className="text-slate-400">Estado</TableHead>
                  <TableHead className="text-slate-400 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {benchmarks.map((benchmark) => (
                  <TableRow key={benchmark.id} className="border-slate-700 hover:bg-slate-800/50">
                    {editingBenchmark?.id === benchmark.id ? (
                      <>
                        <TableCell>
                          <Input
                            value={editingBenchmark.industry}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, industry: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.business_model || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, business_model: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.operational_structure || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, operational_structure: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.category || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, category: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.indicator_name}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, indicator_name: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={editingBenchmark.benchmark_value}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, benchmark_value: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8 w-24"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.source || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, source: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.agent_name || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, agent_name: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingBenchmark.module_type || ''}
                            onChange={(e) => setEditingBenchmark({...editingBenchmark, module_type: e.target.value})}
                            className="bg-slate-900 border-slate-600 text-white h-8"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge className={editingBenchmark.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                            {editingBenchmark.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleUpdateBenchmark}
                              disabled={updateMutation.isPending}
                              className="text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                            >
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingBenchmark(null)}
                              className="text-slate-400 hover:text-slate-300 hover:bg-slate-500/10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell className="text-white font-medium">{benchmark.industry}</TableCell>
                        <TableCell className="text-slate-300">{benchmark.business_model || '-'}</TableCell>
                        <TableCell className="text-slate-300">{benchmark.operational_structure || '-'}</TableCell>
                        <TableCell className="text-slate-300">{benchmark.category || '-'}</TableCell>
                        <TableCell className="text-slate-300">{benchmark.indicator_name}</TableCell>
                        <TableCell>
                          <span className="text-emerald-400 font-mono font-medium">
                            {benchmark.benchmark_value}
                            {benchmark.metric_type === 'percentage' && '%'}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-400 text-xs max-w-[120px] truncate" title={benchmark.source || ''}>
                          {benchmark.source || '-'}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {benchmark.agent_name || '-'}
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {benchmark.module_type || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge className={benchmark.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'}>
                            {benchmark.is_active ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingBenchmark({...benchmark})}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteBenchmark(benchmark.id)}
                              disabled={deleteMutation.isPending}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Benchmark Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Agregar Benchmark</DialogTitle>
            <DialogDescription className="text-slate-400">
              Agrega un nuevo benchmark de industria manualmente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Industria *</Label>
                <Input
                  value={newBenchmark.industry}
                  onChange={(e) => setNewBenchmark({...newBenchmark, industry: e.target.value})}
                  placeholder="Ej: Tecnología, Retail"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Modelo de Negocio</Label>
                <Input
                  value={newBenchmark.business_model}
                  onChange={(e) => setNewBenchmark({...newBenchmark, business_model: e.target.value})}
                  placeholder="Ej: B2C, B2B, SaaS"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Estructura Operativa</Label>
                <Input
                  value={newBenchmark.operational_structure}
                  onChange={(e) => setNewBenchmark({...newBenchmark, operational_structure: e.target.value})}
                  placeholder="Ej: Tienda Física, Cloud"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Categoría</Label>
                <Input
                  value={newBenchmark.category}
                  onChange={(e) => setNewBenchmark({...newBenchmark, category: e.target.value})}
                  placeholder="Ej: Ventas, Logística"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Indicador de Mercado *</Label>
              <Input
                value={newBenchmark.indicator_name}
                onChange={(e) => setNewBenchmark({...newBenchmark, indicator_name: e.target.value})}
                placeholder="Ej: Tasa de Conversión, Churn Rate"
                className="bg-slate-900 border-slate-600 text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-slate-300">Valor Benchmark *</Label>
                <Input
                  type="number"
                  value={newBenchmark.benchmark_value}
                  onChange={(e) => setNewBenchmark({...newBenchmark, benchmark_value: e.target.value})}
                  placeholder="Ej: 3.5"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Fuente</Label>
                <Input
                  value={newBenchmark.source}
                  onChange={(e) => setNewBenchmark({...newBenchmark, source: e.target.value})}
                  placeholder="Ej: McKinsey 2024"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Tipo de Métrica</Label>
                <Select 
                  value={newBenchmark.metric_type} 
                  onValueChange={(v) => setNewBenchmark({...newBenchmark, metric_type: v})}
                >
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-700">
                    <SelectItem value="number">Número</SelectItem>
                    <SelectItem value="percentage">Porcentaje</SelectItem>
                    <SelectItem value="currency">Moneda</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Agente Asociado</Label>
                <Input
                  value={newBenchmark.agent_name}
                  onChange={(e) => setNewBenchmark({...newBenchmark, agent_name: e.target.value})}
                  placeholder="Nombre del agente"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Módulo</Label>
                <Input
                  value={newBenchmark.module_type}
                  onChange={(e) => setNewBenchmark({...newBenchmark, module_type: e.target.value})}
                  placeholder="Ej: revenue, operations"
                  className="bg-slate-900 border-slate-600 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Descripción</Label>
              <Input
                value={newBenchmark.description}
                onChange={(e) => setNewBenchmark({...newBenchmark, description: e.target.value})}
                placeholder="Descripción opcional del benchmark"
                className="bg-slate-900 border-slate-600 text-white mt-1"
              />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="ghost"
                onClick={() => setShowAddDialog(false)}
                className="text-slate-300"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleCreateBenchmark}
                disabled={createMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {createMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Crear Benchmark
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IndustryBenchmarkManager;
